import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanCard } from './KanbanCard';
import { Plus, MoreHorizontal, Trash2, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const KanbanColumn = ({ 
  column, 
  cards, 
  onCreateCard, 
  onDeleteColumn,
  onCardClick,
  projectLabels 
}) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleAddCard = () => {
    if (!newCardTitle.trim()) return;
    onCreateCard(column.id, newCardTitle);
    setNewCardTitle('');
    setIsAddingCard(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCard();
    } else if (e.key === 'Escape') {
      setIsAddingCard(false);
      setNewCardTitle('');
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex-shrink-0 w-72 bg-white/5 rounded-xl flex flex-col max-h-[calc(100vh-200px)] ${
        isDragging ? 'opacity-50' : ''
      }`}
      data-testid={`kanban-column-${column.id}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded transition-colors"
          >
            <GripVertical size={16} className="text-gray-500" />
          </div>
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0" 
            style={{ backgroundColor: column.color }}
          />
          <h3 className="text-white font-medium text-sm truncate">{column.title}</h3>
          <span className="text-gray-500 text-xs ml-1">({cards.length})</span>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAddingCard(true)}
            className="h-7 w-7 p-0 text-gray-400 hover:text-white hover:bg-white/10"
          >
            <Plus size={16} />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-gray-400 hover:text-white hover:bg-white/10"
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1a1d21] border-white/20">
              <DropdownMenuItem 
                className="text-red-400 hover:text-red-300 focus:bg-red-500/10"
                onClick={() => onDeleteColumn(column.id)}
              >
                <Trash2 size={14} className="mr-2" />
                Delete Column
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Cards Container */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        <SortableContext
          items={cards.map(card => card.id)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map(card => (
            <KanbanCard
              key={card.id}
              card={card}
              columnId={column.id}
              onClick={() => onCardClick(card)}
              projectLabels={projectLabels}
            />
          ))}
        </SortableContext>

        {/* Add Card Input */}
        {isAddingCard && (
          <div className="bg-white/10 rounded-lg p-2">
            <Input
              autoFocus
              value={newCardTitle}
              onChange={(e) => setNewCardTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                if (!newCardTitle.trim()) {
                  setIsAddingCard(false);
                }
              }}
              placeholder="Enter card title..."
              className="bg-transparent border-white/20 text-white text-sm placeholder:text-gray-500 mb-2"
              data-testid="new-card-input"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAddCard}
                disabled={!newCardTitle.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7"
              >
                Add Card
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAddingCard(false);
                  setNewCardTitle('');
                }}
                className="text-gray-400 hover:text-white hover:bg-white/10 text-xs h-7"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Card Button (bottom) */}
      {!isAddingCard && (
        <div className="p-2 border-t border-white/5">
          <button
            onClick={() => setIsAddingCard(true)}
            className="w-full py-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex items-center justify-center gap-1"
            data-testid={`add-card-btn-${column.id}`}
          >
            <Plus size={16} />
            Add a card
          </button>
        </div>
      )}
    </div>
  );
};
